import { message, Modal } from "ant-design-vue";
import { defineStore } from "pinia";
import icon_benefit_share from "@/assets/images/TeamEditionPurchase/icon_benefit--share.svg";
import icon_benefit_discount from "@/assets/images/TeamEditionPurchase/icon_benefit--discount.svg";
import icon_benefit_payment from "@/assets/images/TeamEditionPurchase/icon_benefit--payment.svg";
import icon_benefit_team from "@/assets/images/TeamEditionPurchase/icon_benefit--team.svg";
import { formatCurrency } from "@/utils/format";
import { useUserStore } from "./user";
import { switchTeam } from "@/components/GlobleModal/TeamEditionPurchaseModal/service";
import {
  getPricing,
  formatTeamDiscount,
  WECHAT_MAX_AMOUNT,
  TEAM_YEAR_DAYS
} from "@/components/GlobleModal/TeamEditionPurchaseModal/config/teamPricing";
import { TEAM_EDITION_CONFIRM_Z_INDEX } from "@/components/GlobleModal/TeamEditionPurchaseModal/config/zIndex";
import {
  getTeamVersionMeta,
  TEAM_VERSION_LIST,
  type TeamMemberVersion
} from "@/components/GlobleModal/TeamEditionPurchaseModal/config/teamVersions";
import {
  getCommodityInfo,
  getExistCorporateOrder,
  IGetGoodInfoResponse,
  optCorporateOrder,
  queryContractByTransactionId,
  queryOrderStatus,
  teamUpgradeRefundAmount,
  type ITeamUpgradeRefundResponse,
  type TeamUpgradeStatus
} from "@/views/power/service";
import { PayChannel, PayOrderStatus } from "@/types/pay";
import { useEventTracking } from "@/hooks/useEventTracking";
import router from "@/router"; // 引入 router 实例
import { deleteArtwork, shareArtwork } from "@/views/workspace/service";
import { shareTrainModel } from "@/components/SearchBar/service";
import { queryTeamInfo, TeamInfo } from "@/views/group/service";
import { CorporateTransferApplyStatus, ICorporateTransferOrderInfo, ICreateContractResponse, UploadReceiptParams } from "@/types/teamEdition.d";
import { OrderItemResponse } from "@/types/power";
import { resolvePreOrderPayment, PAYMENT_INFO_ERROR } from "@/assets/pay/preOrderPayment";
import { beginTeamScan } from "./teamPayment";
import { submitTeamCorporateOrder, submitCorporateReceipt } from "./teamCorporatePayment";
import { usePayStore } from "./pay";
import { $bus } from "@/mitt";
import { OrderType } from "@/types/platformCenter";
import useGroupStore from "./group";
import { userRightStore } from "@/hooks/useUserRights";
import { closeModalById } from '@/utils/modal'
import TeamOrderPendingPaymentModal from "@/views/power/components/Modal/TeamOrderPendingPaymentModal.vue";
type OrderInfoType = "wechat" | "alipay" | "all";

// 创建订单失败提示语
export const CREATE_ORDER_FAILED_TIP = "请勿频繁操作，稍后再试";

const delay = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));
interface DiscountOption {
  value: string;
  label: string;
  discount: string;
  valueNum: number;
  price: number;
  originPrice: number;
  monthlyPrice: number;
}

interface VersionOption {
  label: string;
  value: TeamMemberVersion;
  price: number;
  monthlyPrice: number;
  discount: number;
}

export enum ModalType {
  OPEN_TEAM_EDITION = "open_team_edition",
  ADD_SEAT = "add_seat",
  /** 续费订购 */
  RENEWAL_ORDER = "renewal_order",
  /** 升级版本（一期仅展示权益与价格） */
  UPGRADE = "upgrade",
  // api资源包支付
  API_RESOURCE_PACKAGE = "api"
}

export enum PurchaseDuration {
  "one_year" = 1,
  "two_year" = 2,
  "three_year" = 3
}

/**
 * 部分线上的团队屏蔽【增购席位】的入口的团队列表
 * 有三个团队给他们赠送了会员天数，增购席位那套计算价格的公式可能不太适用，想写死屏蔽下那三个团队，不给线上直接增购席位
 */
export const BLOCK_ADD_SEAT_TEAM_LIST = ["6", "7", "8"];

/**
 * 支付类型枚举 1-微信 2-支付宝
 */
enum PayTypeEnum {
  "wechat" = 1,
  "alipay" = 2
}

/**
 * orderQuery返回值中的status枚举
 * 订单状态--0:支付中 1:支付成功 2:支付失败 -1:订单不存在
 */
enum OrderStatusEnum {
  "PAYING" = 0,
  "SUCCESS" = 1,
  "FAIL" = 2,
  "NOT_EXIST" = -1
}

interface TeamEditionState {
  corporateReceiptLoading: boolean;
  corporateReceiptRequestId: number;
  corporateUploadedReceipt: UploadReceiptParams | null;
  corporateOrderLoading: boolean;
  corporateOrderRequestId: number;
  scanRequestId: number;
  scanPaymentError: boolean;
  modalOpen: boolean;
  // 切换个人/团队弹窗
  switchTypeModalOpen: boolean;
  // 显示联系客服弹窗
  contactServiceModalOpen: boolean;
  // 联系客服可以是申请合同或者咨询价格
  contractServiceType: "application" | "pricing" | "member";
  // 当前第几步
  currentStep: number;
  userType: "personal" | "team";
  formData: {
    teamName: string;
    memberCount: number;
    // 会员版本
    selectedVersion: TeamMemberVersion;
    // 购买时长（商业化调优后仅售 1 年，保留字段用于下单接口 duration 参数）
    purchaseDuration: "one_year";
  };
  paymentMethod: "wechat" | "alipay";
  transferInfo: {
    // 企业全称
    companyName: string;
    // 联系电话
    phone: string;
    // 付款回执
    paymentProof: string;
  };
  remittanceInfo: {
    // 企业全称
    companyName: string;
    // 银行账号
    bankAccount: string;
    // 开户行
    bankName: string;
    // 转账备注
    remark: string;
  };
  addOrderResponse: {
    qrCode: string;
    innerOrderId: string;
  };
  wechatOrderInfo: {
    qrCode: string;
    innerOrderId: string;
  };
  orderStatus?: PayOrderStatus;
  // 添加 getter 返回类型
  durationValue?: number;
  // 是否显示团队空间引导弹窗
  isShowTeamSpaceGuide: boolean;
  // 是否显示共享至团队空间弹窗
  isShowShareToTeamLoadingModal: boolean;
  // 是否显示共享成功弹窗
  isShowShareToTeamSuccessToast: boolean;
  // 当前弹窗类型
  modalType: ModalType;
  teamInfo: TeamInfo | null;
  addSeat: {
    /** 总天数 */
    totalDays: number;
    /** 剩余使用天数 */
    remainingDays: number;
    /** 当前已购买的席位数 */
    currentSeatCount: number;
  };
  /** 合同申请弹窗 */
  contractApplyModalOpen: boolean;
  /** 合同查看弹窗 */
  contractViewModalOpen: boolean;
  /** 是否使用对公转账 */
  isUseCorporateTransfer: boolean;
  /** 对公转账预下单订单信息 */
  corporateTransferOrderInfo: ICorporateTransferOrderInfo;
  /** 对公转账申请状态 */
  corporateTransferApplyStatus: CorporateTransferApplyStatus;
  /** 中台商品信息 */
  goodInfo: IGetGoodInfoResponse;
  /** 申请合同返回数据 */
  createContractResponse: ICreateContractResponse;
  /** 是否正在刷新请求参数 */
  refreshRequestParams: boolean;
  /** 是否从新客AB弹窗打开 */
  isOpenFromNewCustomerAB: boolean;
  /** 新客AB弹窗激活的tab */
  customerABActiveTab: "member" | "power" | "groupMember" | "";
  /** 订单是否支付成功 */
  isAddSetOrderPaySuccess: boolean;
  /**
   * 升级预校验信息（teamUpgradeRefundAmount 返回）
   * 仅用于 upStatus 拦截与文案展示；实际支付金额一律以 getCommodityInfo 为准（见 upgradeOrderAmount）
   */
  upgradeRefund: {
    /** 升级可用性状态：0-可升级 1-需人工介入 2-不支持；null 表示尚未校验 */
    status: TeamUpgradeStatus | null;
    /** 不支持/异常时的原因文案（upStatus=2 取自 oldSubject） */
    reason: string;
    /** 是否正在校验中 */
    loading: boolean;
    /** 原始返回，便于后续扩展 */
    detail: ITeamUpgradeRefundResponse | null;
  };
  /**
   * 升级实付补差金额（元）——以 getCommodityInfo 返回的 totalAmount（分）换算而来
   * 独立于 totalAmount getter，避免污染续费/增席/开通的既有金额展示逻辑
   */
  upgradeOrderAmount: number;
}

export const benefits = [
  {
    title: "灵活采购/权益共享",
    desc: "一人支付，全团队使用；团队算力/会员权益共享",
    // 使用 icon_benefit--share 图标
    icon: icon_benefit_share
  },
  {
    title: "阶梯折扣优惠",
    desc: "根据团队人数采购，人数越多，折扣越大",
    // 使用 icon_benefit--discount 图标
    icon: icon_benefit_discount
  },
  {
    title: "团队空间/共享协作",
    desc: "自训练模型、作品可在团队内共享，团队成员可一键使用、二次创作，协作更高效！",
    // 使用 icon_benefit--discount 图标
    icon: icon_benefit_team
  },
  {
    title: "便捷支付/发票服务",
    desc: "支持微信/支付宝，提供发票服务",
    // 使用 icon_benefit--payment 图标
    icon: icon_benefit_payment
  }
];

/**
 * 开通团队版的步骤
 */
export const OPEN_TEAM_EDITION_STEPS = ["第一步：提交购买订单", "第二步：支付订单", "第三步：成功开通新团队"];

// 增加席位的步骤
export const ADD_SEAT_STEPS = ["第一步：提交购买订单", "第二步：支付订单", "第三步：支付成功"];

// 最大可购买席位数
export const MAX_SEAT_COUNT = 100;

/** 新增席位购买提示 */
export const addSeatPurchaseTips = [
  `*团队总席位数满 5 人、满 10 人可享更高人数阶梯折扣（团队总席位数最多支持 ${MAX_SEAT_COUNT} 人）`,
  "*新增席位所使用的会员版本、到期时间与团队整体一致，到期后统一失效，付款金额按剩余使用天数进行计算",
  "*新增席位订单支付成功后，系统将按新的席位数按月发放团队算力"
];

// 空间类型tabs
export const spaceTypeTabs = [
  {
    title: "个人空间",
    key: "personal"
  },
  {
    title: "团队空间",
    key: "team"
  }
];

interface Timer {
  wechat: NodeJS.Timeout | null;
  alipay: NodeJS.Timeout | null;
}

// 修改timer声明
let timer: Timer = {
  wechat: null,
  alipay: null
};

// 升级报价/下单请求序列，用于丢弃切换版本或支付方式后返回的旧响应
let upgradeQuoteRequestId = 0;
let upgradeOrderRequestId = 0;

export const useTeamEditionStore = defineStore("teamEdition", {
  state: (): TeamEditionState => ({
    corporateReceiptLoading: false,
    corporateReceiptRequestId: 0,
    corporateUploadedReceipt: null,
    corporateOrderLoading: false,
    corporateOrderRequestId: 0,
    scanRequestId: 0,
    scanPaymentError: false,
    modalOpen: !true,
    switchTypeModalOpen: false,
    // 联系客服类型
    contractServiceType: "pricing",
    // 显示联系客服弹窗
    contactServiceModalOpen: false,
    // 当前第几步
    currentStep: 1,
    // 当前用户账号类型
    userType: "personal",
    // 表单数据
    formData: {
      teamName: "",
      memberCount: 2,
      selectedVersion: "pro",
      purchaseDuration: "one_year"
    },
    // 对公转账信息
    transferInfo: {
      companyName: "",
      phone: "",
      paymentProof: ""
    },
    // 汇款信息
    remittanceInfo: {
      companyName: "test-企业全称",
      bankAccount: "test-银行账号",
      bankName: "test-开户行",
      remark: "test-转账备注"
    },
    // 支付方式
    paymentMethod: "alipay",
    // 添加订单后的响应信息
    addOrderResponse: {
      qrCode: "",
      innerOrderId: ""
    },
    wechatOrderInfo: {
      qrCode: "",
      innerOrderId: ""
    },
    /** 对公转账预下单订单信息 */
    corporateTransferOrderInfo: {} as ICorporateTransferOrderInfo,
    orderStatus: undefined,
    isShowTeamSpaceGuide: false,
    isShowShareToTeamLoadingModal: false,
    isShowShareToTeamSuccessToast: false,
    teamInfo: null,
    modalType: ModalType.ADD_SEAT,
    addSeat: {
      currentSeatCount: 0,
      remainingDays: 1,
      totalDays: 1
    },
    // 合同申请弹窗
    contractApplyModalOpen: false,
    // 合同查看弹窗
    contractViewModalOpen: false,
    /** 是否使用对公转账 */
    isUseCorporateTransfer: false,
    /** 对公转账申请状态 */
    corporateTransferApplyStatus: CorporateTransferApplyStatus.NOT_APPLY,
    /** 中台商品信息 */
    goodInfo: {} as IGetGoodInfoResponse,
    /** 申请合同返回数据 */
    createContractResponse: {} as ICreateContractResponse,
    /** 是否正在刷新请求参数 */
    refreshRequestParams: false,
    /** 是否从新客AB弹窗打开 */
    isOpenFromNewCustomerAB: false,
    /** 新客AB弹窗激活的tab */
    customerABActiveTab: "",
    /** 订单是否支付成功 */
    isAddSetOrderPaySuccess: false,
    /** 升级预校验信息（仅用于 upStatus 拦截与文案；金额以 getCommodityInfo 为准） */
    upgradeRefund: {
      status: null,
      reason: "",
      loading: false,
      detail: null
    },
    /** 升级实付补差金额（元） */
    upgradeOrderAmount: 0
  }),
  actions: {
    // 判断是否存在对公转账订单
    async judgeExistCorporateOrder() {
      const res = await getExistCorporateOrder();
      return res.data;
    },
    setModalOpen(open: boolean) {
      this.modalOpen = open;
      if (!open) {
        // 关闭弹窗时清除所有定时器
        this.clearTimer();
      } else {
        // 打开弹窗时，重新启动相关定时器
        this.restartActiveTimers();
      }
    },
    // 设置弹窗类型
    setModalType(type: ModalType) {
      this.modalType = type;
    },
    setPaymentMethod(method: "wechat" | "alipay") {
      this.paymentMethod = method;
    },
    //  跳转到团队管理页面
    goToTeamManagement() {
      // 关闭弹窗
      this.setModalOpen(false);
      // 如果需要确保页面刷新
      location.href = "/group/space";
    },
    // 跳转到团队版会员中心
    goToTeamEditionCenter() {
      router.push({
        name: "group-space"
      });
    },
    // 切换个人版和团队版
    async switchUserType(objs?: Record<"successCallback", any>) {
      await switchTeam();

      // 切换成功时，更新用户权益信息
      const userRightsHook = userRightStore();
      await userRightsHook.refreshUserRights();

      // 关闭消息弹窗
      this.setModalOpen(false);

      /**
       * 这里已经在refreshUserRights接口中处理了用户类型切换
       */
      let isPersonal = this.userType === "personal";
      window.onbeforeunload = null;

      useEventTracking(
        {
          event: "click",
          eventValue: {
            button: isPersonal ? "switch_version_team" : "switch_version_personal"
          }
        },
        30
      );

      // 默认切换成功回调
      const defaultSuccessCallback = async () => {
        await delay(30);
        // 储存切换到的用户类型
        localStorage.setItem("teamEditionUserType", isPersonal ? "personal" : "team");
        // 跳转到首页并刷新页面
        try {
          router.replace("/");
          location.reload();
        } catch (error) {
          console.error("导航错误:", error);
        }
      };

      if (typeof objs?.successCallback === "function") {
        objs?.successCallback();
      } else {
        defaultSuccessCallback();
      }

      // message.success(`已切换至【${isPersonal ? '团队版' : '个人版'}】`);
    },
    setSwitchTypeModalOpen(open: boolean) {
      this.switchTypeModalOpen = open;
    },
    // async getOrderStatus(innerOrderId: string, onSuccess: () => void, onError: (error: any) => void) {
    //     try {
    //         const { desc, status } = await XMSDKWebJs.orderQuery(innerOrderId);
    //         if ([OrderStatusEnum.SUCCESS].includes(status)) {
    //             typeof onSuccess === 'function' && onSuccess();
    //         } else if ([OrderStatusEnum.FAIL, OrderStatusEnum.NOT_EXIST].includes(status)) {
    //             typeof onError === 'function' && onError(status);
    //         }
    //     } catch (error) {
    //         typeof onError === 'function' && onError(error);
    //     }
    // },
    async getOrderStatus(innerOrderId: string, onSuccess: () => void, onError: (error: any) => void, isActive: () => boolean = () => true) {
      try {
        const { data } = await queryOrderStatus({ transactionId: innerOrderId });
        if (!isActive()) return;
        const { orderStatus } = data;
        this.orderStatus = data;
        if (["SUCCESS", "TRADE_SUCCESS"].includes(orderStatus)) {
          typeof onSuccess === "function" && onSuccess();
        } else if (["PAYERROR", "TRADE_CLOSED"].includes(orderStatus)) {
          typeof onError === "function" && onError(orderStatus);
        }
      } catch (error) {
        if (!isActive()) return;
        typeof onError === "function" && onError(error);
      }
    },
    // 清空定时器
    clearTimer() {
      this.scanRequestId++;
      (Object.keys(timer) as Array<keyof typeof timer>).forEach((key) => {
        timer[key] && clearInterval(timer[key]);
        timer[key] = null;
      });
    },
    // 清空订单信息
    resetOrderInfo(type: OrderInfoType = "all") {
      this.clearTimer();
      this.scanPaymentError = false;
      const defaultOrderInfo = {
        qrCode: "",
        innerOrderId: ""
      };

      switch (type) {
        case "wechat":
          this.wechatOrderInfo = { ...defaultOrderInfo };
          break;
        case "alipay":
          this.addOrderResponse = { ...defaultOrderInfo };
          break;
        case "all":
        default:
          this.wechatOrderInfo = { ...defaultOrderInfo };
          this.addOrderResponse = { ...defaultOrderInfo };
      }
    },
    resetFormData() {
      this.formData = {
        teamName: "",
        memberCount: 2,
        selectedVersion: "pro",
        purchaseDuration: "one_year"
      };
      this.currentStep = 1;
    },
    resetAddSeat() {
      this.addSeat = {
        currentSeatCount: 0,
        remainingDays: 1,
        totalDays: 1
      };
    },
    // 新增的queryStatus方法
    queryStatus(innerOrderId: string, paymentType: "wechat" | "alipay") {
      const isActive = beginTeamScan(this);
      const timerKey = paymentType;
      // 先清除已存在的定时器
      if (timer[timerKey]) {
        clearInterval(timer[timerKey]);
      }

      /**
       * 宿主弹窗打开时才启动定时器（含团队新开通支付凭证弹窗），且不是对公转账
       */
      if ((this.modalOpen || this.productModalIsOpen || this.teamMemberPayModalOpen) && !this.isUseCorporateTransfer) {
        timer[timerKey] = setInterval(async () => {
          if (!isActive()) return;

          this.getOrderStatus(
            innerOrderId,
            async () => {
              if (!isActive()) return;
              this.clearTimer();
              const isSuccessActive = beginTeamScan(this);
              const payStore = usePayStore();
              // 保留成功展示延迟；延迟期间关闭弹窗仍应取消后续 UI。
              await delay(3000);
              if (!isSuccessActive()) return;
              this.clearTimer();
              payStore.productModalState.open = false;
              // 先打开成功引导宿主，再关支付凭证弹窗，避免扫码会话在间隙变为 inactive
              message.success("支付成功");
              this.modalOpen = true;
              this.currentStep = 3;
              payStore.payModalState.open = false;

              // 关闭挽留弹窗
              closeModalById(TeamOrderPendingPaymentModal.modalId);

              this.handleClickEvent({
                team_order_number: this.formData.memberCount, // 团队人数
                team_order_version: getTeamVersionMeta(this.formData.selectedVersion).label, // 标准版/专业版/旗舰版
                team_order_time: `${this.durationValue}年`, // 购买时长
                team_order_id: innerOrderId, // 订单id
                team_order_amount: this.totalAmount // 订单总金额
              });

              // 如果是增席，则需要更新席位信息
              if (this.modalType === ModalType.ADD_SEAT) {
                this.isAddSetOrderPaySuccess = true;
                // this.resetOrderInfo();

                // this.formData = {
                //     teamName: '',
                //     memberCount: 2,
                //     selectedVersion: 'pro',
                //     purchaseDuration: 'one_year'
                // };
              }

              //团队版信息更新
              // NOTE: 升级同样会改变团队版本/权益，需要刷新团队信息（与增席/开通对称）
              if ([ModalType.ADD_SEAT, ModalType.OPEN_TEAM_EDITION, ModalType.UPGRADE].includes(this.modalType)) {
                const groupStore = useGroupStore();
                groupStore.refreshGroup();
              }

              const userStore = useUserStore();
              userStore.queryUserInfo();
              userStore.queryUserRights();
            },
            () => {
              if (!isActive()) return;
              message.error("支付失败");
              this.clearTimer();
            },
            isActive
          );
        }, 2000);
      }
    },
    async handleAddOrder(successCallback?: () => void) {
      const isActive = beginTeamScan(this);
      try {
        if (!isActive()) return;
        // 如果已经存在订单，则不创建订单
        if (this.paymentMethod === "wechat" && this.wechatOrderInfo.innerOrderId) {
          console.warn("微信订单已存在");
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.wechat) {
            this.queryStatus(this.wechatOrderInfo.innerOrderId, "wechat");
          }
          return;
        }
        if (this.paymentMethod === "alipay" && this.addOrderResponse.innerOrderId) {
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.alipay) {
            this.queryStatus(this.addOrderResponse.innerOrderId, "alipay");
          }
          return;
        }

        // 获取预下单商品信息
        const res = await getCommodityInfo({
          payModel: "pc",
          headCount: this.formData.memberCount,
          teamName: this.sysTeamName,
          memberType: this.formData.selectedVersion,
          duration: this.formData.purchaseDuration,
          orderType: "team_member",
          payChannel: this.paymentMethod as PayChannel
        });

        if (!isActive()) return;
        this.orderStatus = res.data as any;

        let type = PayTypeEnum[this.paymentMethod];
        const aymentQRCodeRes = resolvePreOrderPayment(res.data, "order");

        const team_order_type = `团队版-${getTeamVersionMeta(this.formData.selectedVersion).shortLabel}${this.durationValue}年会员`;
        this.handleClickEvent({
          button: "order_team_submit",
          team_order_id: aymentQRCodeRes.innerOrderId, // 订单id
          team_order_type,
          team_order_amount: this.totalAmount, // 订单总金额
          order_team_number: this.formData.memberCount, // 团队人数
          order_team_version: getTeamVersionMeta(this.formData.selectedVersion).label, // 标准版/专业版/旗舰版
          order_team_time: `${this.durationValue}年` // 购买时长
        });

        const innerOrderId = aymentQRCodeRes.innerOrderId;
        if (type === PayTypeEnum.wechat) {
          this.wechatOrderInfo = aymentQRCodeRes;
        } else {
          this.addOrderResponse = aymentQRCodeRes;
        }

        typeof successCallback === "function" && successCallback();

        // 调用提取出的queryStatus方法
        if (isActive()) this.queryStatus(innerOrderId, this.paymentMethod);
      } catch (error: any) {
        if (!isActive()) return;
        this.scanPaymentError = true;
        // 判断error的code为80000时，代表账号已被团队拉入
        if (error?.code === 80000) {
          // 获取用户信息
          const userStore = useUserStore();
          userStore.queryUserInfo();
          message.warn("当前账号已加入团队");
        } else {
          message.error(PAYMENT_INFO_ERROR);
          $bus.emit("groupMemberPayError");
        }
        const timerKey = this.paymentMethod as keyof typeof timer;
        timer[timerKey] && clearInterval(timer[timerKey]);
      }
    },
    // 点击事件埋点
    handleClickEvent(eventValue: any) {
      useEventTracking({
        event: "click",
        eventValue
      });
    },
    // 弹出的弹窗类型
    handleBallEvent(eventValue: any) {
      useEventTracking({
        event: "ball",
        eventValue
      });
    },
    restartActiveTimers() {
      if (this.wechatOrderInfo.innerOrderId && this.paymentMethod === "wechat") {
        this.queryStatus(this.wechatOrderInfo.innerOrderId, "wechat");
      }
      if (this.addOrderResponse.innerOrderId && this.paymentMethod === "alipay") {
        // 接口有同时调用的限制，所以错开调用
        setTimeout(() => {
          this.queryStatus(this.addOrderResponse.innerOrderId, "alipay");
        }, 1000);
      }
    },
    // 将作品共享到团队
    async handleShareArtwork(artworkId: number, versionId: number) {
      this.isShowShareToTeamLoadingModal = true;

      try {
        const res = await shareArtwork({
          artworkId,
          versionId
        });

        if (res.code === 0) {
          await delay(500);
          this.isShowShareToTeamLoadingModal = false;
          this.isShowShareToTeamSuccessToast = true;
        }
      } catch (error) {
        this.isShowShareToTeamLoadingModal = false;
      }
    },
    // 共享至团队空间
    async handleModelShare(id: number) {
      this.isShowShareToTeamLoadingModal = true;

      try {
        const res = await shareTrainModel({
          id
        });

        if (res.code === 0) {
          await delay(500);
          this.isShowShareToTeamLoadingModal = false;
          this.isShowShareToTeamSuccessToast = true;
        }
      } catch (error) {
        this.isShowShareToTeamLoadingModal = false;
      }
    },
    // 共享训练模型
    async handleTrainModelShare(id: number) {
      this.isShowShareToTeamLoadingModal = true;

      try {
        const res = await shareTrainModel({
          id
        });
        if (res.code === 0) {
          await delay(500);
          this.isShowShareToTeamLoadingModal = false;
          this.isShowShareToTeamSuccessToast = true;
        }
      } catch (error) {
        this.isShowShareToTeamLoadingModal = false;
      }
    },
    // 删除收藏的作品
    async handleDeleteArtwork(id: number) {
      return new Promise(async (resolve, reject) => {
        try {
          let res = await deleteArtwork({
            id
          });
          if (res.code === 0) {
            message.success("删除成功");
            const userStore = useUserStore();
            await userStore.queryUserRights();
            resolve(true);
          } else {
            reject(res.msg);
          }
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
    },
    async openAddSeatModal() {
      if (this.currentStep === 1) {
        this.formData.memberCount = 1;
        await this.getTeamInfo();
        this.setModalType(ModalType.ADD_SEAT);
      }
      this.setModalOpen(true);
    },
    async getTeamInfo() {
      const { data } = await queryTeamInfo();
      this.teamInfo = data;

      const { memberType, leaveDay, memberDay } = data;

      // 边界收口：接口 memberType 可能为 free/脏值，统一经注册表兜底
      this.formData.selectedVersion = getTeamVersionMeta(memberType).value;
      // 仅售 1 年：历史多年期团队的原始时长不再参与定价（增购按剩余天数折算，见 remainingDaysRatio）
      this.formData.purchaseDuration = "one_year";

      this.addSeat.currentSeatCount = this.teamInfo?.headCount ?? 0;
      this.addSeat.remainingDays = leaveDay;
      this.addSeat.totalDays = memberDay;
    },
    async handleAddSeatOrder(successCallback?: () => void) {
      const isActive = beginTeamScan(this);
      try {
        if (!isActive()) return;
        // 如果已经存在订单，则不创建订单
        if (this.paymentMethod === "wechat" && this.wechatOrderInfo.innerOrderId) {
          console.warn("微信订单已存在");
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.wechat) {
            this.queryStatus(this.wechatOrderInfo.innerOrderId, "wechat");
          }
          return;
        }
        if (this.paymentMethod === "alipay" && this.addOrderResponse.innerOrderId) {
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.alipay) {
            this.queryStatus(this.addOrderResponse.innerOrderId, "alipay");
          }
          return;
        }

        // 获取预下单商品信息
        const res = await getCommodityInfo({
          payModel: "pc",
          addHeadCount: this.formData.memberCount,
          teamId: this.teamInfo?.teamId,
          orderType: "team_add_member",
          payChannel: this.paymentMethod as PayChannel
        });

        if (!isActive()) return;
        this.orderStatus = res.data as any;

        const aymentQRCodeRes = resolvePreOrderPayment(res.data, "order");

        const innerOrderId = aymentQRCodeRes.innerOrderId;
        if (this.paymentMethod === "wechat") {
          this.wechatOrderInfo = aymentQRCodeRes;
        } else {
          this.addOrderResponse = aymentQRCodeRes;
        }

        typeof successCallback === "function" && successCallback();

        // 调用提取出的queryStatus方法
        if (isActive()) this.queryStatus(innerOrderId, this.paymentMethod);
      } catch (error: any) {
        if (!isActive()) return;
        this.scanPaymentError = true;
        // 判断error的code为80000时，代表账号已被团队拉入
        if (error.code === 80000) {
          // 获取用户信息
          const userStore = useUserStore();
          userStore.queryUserInfo();
          message.warn("当前账号已加入团队");
        } else {
          message.error(PAYMENT_INFO_ERROR);
          $bus.emit("groupMemberPayError");
        }
        const timerKey = this.paymentMethod as keyof typeof timer;
        timer[timerKey] && clearInterval(timer[timerKey]);
      }
    },
    /**
     * 续费订购
     */
    async handleRenewalOrder(successCallback?: () => void) {
      const isActive = beginTeamScan(this);
      try {
        if (!isActive()) return;
        // 如果已经存在订单，则不创建订单
        if (this.paymentMethod === "wechat" && this.wechatOrderInfo.innerOrderId) {
          console.warn("微信订单已存在");
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.wechat) {
            this.queryStatus(this.wechatOrderInfo.innerOrderId, "wechat");
          }
          return;
        }
        if (this.paymentMethod === "alipay" && this.addOrderResponse.innerOrderId) {
          // 判断订单状态是否在查询中，如果没有，则开启查询
          if (!timer.alipay) {
            this.queryStatus(this.addOrderResponse.innerOrderId, "alipay");
          }
          return;
        }

        // 获取预下单商品信息
        const res = await getCommodityInfo({
          payModel: "pc",
          headCount: this.formData.memberCount,
          teamName: this.sysTeamName,
          memberType: this.formData.selectedVersion,
          duration: this.formData.purchaseDuration,
          orderType: "team_member",
          payChannel: this.paymentMethod as PayChannel
        });

        if (!isActive()) return;
        this.orderStatus = res.data as any;

        let type = PayTypeEnum[this.paymentMethod];
        const aymentQRCodeRes = resolvePreOrderPayment(res.data, "order");

        const team_order_type = `团队版-${getTeamVersionMeta(this.formData.selectedVersion).shortLabel}${this.durationValue}年会员`;
        this.handleClickEvent({
          button: "order_team_submit",
          team_order_id: aymentQRCodeRes.innerOrderId, // 订单id
          team_order_type,
          team_order_amount: this.totalAmount, // 订单总金额
          order_team_number: this.formData.memberCount, // 团队人数
          order_team_version: getTeamVersionMeta(this.formData.selectedVersion).label, // 标准版/专业版/旗舰版
          order_team_time: `${this.durationValue}年` // 购买时长
        });

        const innerOrderId = aymentQRCodeRes.innerOrderId;
        if (type === PayTypeEnum.wechat) {
          this.wechatOrderInfo = aymentQRCodeRes;
        } else {
          this.addOrderResponse = aymentQRCodeRes;
        }

        typeof successCallback === "function" && successCallback();

        // 调用提取出的queryStatus方法
        if (isActive()) this.queryStatus(innerOrderId, this.paymentMethod);
      } catch (error: any) {
        if (!isActive()) return;
        this.scanPaymentError = true;
        // 判断error的code为80000时，代表账号已被团队拉入
        if (error?.code === 80000) {
          // 获取用户信息
          const userStore = useUserStore();
          userStore.queryUserInfo();
          message.warn("当前账号已加入团队");
        } else {
          message.error(PAYMENT_INFO_ERROR);
          $bus.emit("groupMemberPayError");
        }
        const timerKey = this.paymentMethod as keyof typeof timer;
        timer[timerKey] && clearInterval(timer[timerKey]);
      }
    },
    /**
     *  开通团队版本使用对公支付
     */
    async handleAddCorporateTransfer(successCallback?: () => void) {
      return submitTeamCorporateOrder(this, {
        headCount: this.formData.memberCount,
        teamName: this.sysTeamName,
        memberType: this.formData.selectedVersion,
        duration: this.formData.purchaseDuration,
        orderType: "team_member",
        payChannel: "corporate"
      }, successCallback);
    },
    /** 增加席位使用对公转账，渠道不沿用当前扫码方式。 */
    async handleAddSeatCorporateTransfer(successCallback?: () => void) {
      return submitTeamCorporateOrder(this, {
        addHeadCount: this.formData.memberCount,
        teamId: this.teamInfo?.teamId,
        orderType: "team_add_member",
        payChannel: "corporate"
      }, successCallback);
    },
    /**
     * 升级预校验：拉取 upStatus 与补差参考信息
     * NOTE: 仅用于可用性拦截与文案展示；实际支付金额一律以 getCommodityInfo 为准
     * 触发时机：进入升级 Tab、切换目标版本时调用
     */
    async queryTeamUpgradeRefund() {
      const requestId = ++upgradeQuoteRequestId;
      const target = this.formData.selectedVersion;
      // 刷新报价时，同时使正在进行的旧版本下单失效
      upgradeOrderRequestId++;
      // 切换目标版本时清理旧报价，避免展示或支付方式沿用上一版本的补差金额
      this.upgradeOrderAmount = 0;
      this.upgradeRefund = { status: null, reason: "", loading: true, detail: null };
      // 升级目标只能是 plus / ultra；pro 不是有效的升级目标，直接跳过校验
      if (target !== "plus" && target !== "ultra") {
        this.upgradeRefund = { status: 1, reason: "当前版本无需升级", loading: false, detail: null };
        return;
      }
      const teamId = this.teamInfo?.teamId;
      if (teamId == null) {
        this.upgradeRefund = { status: 1, reason: "团队信息加载失败，请刷新重试", loading: false, detail: null };
        return;
      }
      try {
        const res = await teamUpgradeRefundAmount({
          memberType: target,
          orderType: "team_member",
          teamId
        });
        if (requestId !== upgradeQuoteRequestId || this.formData.selectedVersion !== target) {
          return;
        }
        const detail = res.data;
        // upStatus=2 时 oldSubject 承载不支持原因；upStatus=1 为需人工介入
        this.upgradeRefund = {
          status: detail.upStatus,
          reason: detail.upStatus === 0 ? "" : detail.oldSubject || "",
          loading: false,
          detail
        };
        // Payment 组件常驻，报价完成后主动生成当前支付方式的二维码。
        if (
          detail.upStatus === 0 &&
          this.modalType === ModalType.UPGRADE &&
          this.productModalIsOpen &&
          !this.isUseCorporateTransfer
        ) {
          void this.handleUpgradeOrder();
        }
      } catch (error) {
        if (requestId !== upgradeQuoteRequestId || this.formData.selectedVersion !== target) {
          return;
        }
        // 校验失败按“需人工介入”处理，阻断下单避免错误订单
        this.upgradeRefund = {
          status: 1,
          reason: "",
          loading: false,
          detail: null
        };
      }
    },
    /**
     * 进入升级场景时同步清理旧报价与订单，并使所有旧异步请求失效。
     */
    invalidateUpgradeState() {
      upgradeQuoteRequestId++;
      upgradeOrderRequestId++;
      this.upgradeOrderAmount = 0;
      this.upgradeRefund = { status: null, reason: "", loading: true, detail: null };
      this.clearTimer();
      this.resetOrderInfo();
    },
    /**
     * 升级订购（扫码支付：微信 / 支付宝）
     * 与续费的区别：升级保持团队当前席位与周期不变，getCommodityInfo 需传
     * teamId + memberType + duration + headCount（后端据此定位团队并计算补差），
     * 金额以 getCommodityInfo 返回的 totalAmount 为准，落到 upgradeOrderAmount
     */
    async handleUpgradeOrder(successCallback?: () => void) {
      const requestId = ++upgradeOrderRequestId;
      const targetVersion = this.formData.selectedVersion;
      const requestedPaymentMethod = this.paymentMethod;
      const isActive = beginTeamScan(this);
      try {
        if (!isActive()) return;
        // 预校验尚未完成，静默等待（queryTeamUpgradeRefund 完成后会自动触发本函数）
        if (this.upgradeRefund.status === null) return;
        // 升级不可用时直接拦截，避免生成错误订单
        if (this.upgradeRefund.status !== 0) {
          message.warn(this.upgradeRefund.reason || "当前暂不支持升级，请联系客服");
          return;
        }
        // 如果已经存在订单，则不创建订单
        if (this.paymentMethod === "wechat" && this.wechatOrderInfo.innerOrderId) {
          console.warn("微信订单已存在");
          if (!timer.wechat) {
            this.queryStatus(this.wechatOrderInfo.innerOrderId, "wechat");
          }
          return;
        }
        if (this.paymentMethod === "alipay" && this.addOrderResponse.innerOrderId) {
          if (!timer.alipay) {
            this.queryStatus(this.addOrderResponse.innerOrderId, "alipay");
          }
          return;
        }

        // 获取预下单商品信息（升级口径：传 teamId + memberType + duration + headCount，
        // headCount 为团队当前席位数，升级不改席位；后端据此计算补差金额）
        const res = await getCommodityInfo({
          payModel: "pc",
          teamId: this.teamInfo?.teamId,
          memberType: this.formData.selectedVersion,
          orderType: "team_member",
          payChannel: this.paymentMethod as PayChannel,
          duration: this.formData.purchaseDuration,
          headCount: this.teamInfo?.headCount
        });

        if (
          requestId !== upgradeOrderRequestId ||
          this.formData.selectedVersion !== targetVersion ||
          this.paymentMethod !== requestedPaymentMethod
        ) {
          return;
        }

        if (!isActive()) return;
        this.orderStatus = res.data as any;
        // 记录实付补差金额（元），供升级模式金额展示统一取用
        this.upgradeOrderAmount = (res.data.totalAmount || 0) / 100;

        // 后端实付金额是最终口径；若预校验与实付结果不一致，禁止继续申请微信二维码。
        if (requestedPaymentMethod === "wechat" && this.upgradeOrderAmount > WECHAT_MAX_AMOUNT) {
          this.paymentMethod = "alipay";
          return;
        }

        const type = PayTypeEnum[requestedPaymentMethod];
        const aymentQRCodeRes = resolvePreOrderPayment(res.data, "order");

        if (
          requestId !== upgradeOrderRequestId ||
          this.formData.selectedVersion !== targetVersion ||
          this.paymentMethod !== requestedPaymentMethod
        ) {
          return;
        }

        const innerOrderId = aymentQRCodeRes.innerOrderId;
        if (type === PayTypeEnum.wechat) {
          this.wechatOrderInfo = aymentQRCodeRes;
        } else {
          this.addOrderResponse = aymentQRCodeRes;
        }

        typeof successCallback === "function" && successCallback();

        if (isActive()) this.queryStatus(innerOrderId, requestedPaymentMethod);
      } catch (error: any) {
        if (!isActive()) return;
        this.scanPaymentError = true;
        if (requestId !== upgradeOrderRequestId) {
          return;
        }
        if (error?.code === 80000) {
          const userStore = useUserStore();
          userStore.queryUserInfo();
          message.warn("当前账号已加入团队");
        } else {
          message.error(PAYMENT_INFO_ERROR);
          $bus.emit("groupMemberPayError");
        }
        const timerKey = requestedPaymentMethod as keyof typeof timer;
        timer[timerKey] && clearInterval(timer[timerKey]);
      }
    },
    /**
     * 升级使用对公转账
     */
    async handleUpgradeCorporateTransfer(successCallback?: () => void) {
      if (this.upgradeRefund.status === null) return;
      if (this.upgradeRefund.status !== 0) {
        message.warn(this.upgradeRefund.reason || "当前暂不支持升级，请联系客服");
        return;
      }
      return submitTeamCorporateOrder(this, {
        teamId: this.teamInfo?.teamId,
        memberType: this.formData.selectedVersion,
        orderType: "team_member",
        payChannel: "corporate",
        duration: this.formData.purchaseDuration,
        headCount: this.teamInfo?.headCount
      }, (result) => {
        this.upgradeOrderAmount = result.commodity.totalAmount! / 100;
        successCallback?.();
      });
    },
    /**
     * 上传对公回执
     */
    async handleUploadReceipt(data: UploadReceiptParams) {
      return submitCorporateReceipt(this, data);
    },
    /**
     *  检查合同是否已生成
     */
    async checkContract() {
      if (!this.corporateTransferOrderInfo.orderId) {
        console.warn("订单id不存在");
        return;
      }
      try {
        const orderId = this.corporateTransferOrderInfo.orderId;
        const contract = await queryContractByTransactionId(orderId);
        if (orderId === this.corporateTransferOrderInfo.orderId && contract?.data) {
          this.createContractResponse = contract.data;
        }
      } catch (err) {
        console.log(err);
      }
    },
    // 退出登录时，清空所有返回数据
    resetResponseData() {
      this.corporateUploadedReceipt = null;
      this.createContractResponse = {} as ICreateContractResponse;
      this.corporateTransferOrderInfo = {} as ICorporateTransferOrderInfo;
      this.orderStatus = undefined;
      this.modalOpen = false;
      this.currentStep = 1;
      this.isUseCorporateTransfer = false;
      this.corporateTransferApplyStatus = CorporateTransferApplyStatus.NOT_APPLY;
    },
    /**
     * 打开上传回执
     */
    async handleOpenUploadReceipt() {
      this.isUseCorporateTransfer = true;
      await this.checkContract();
      this.modalOpen = true;
      this.currentStep = 2;
    },
    /**
     * 打开对公支付待确认
     */
    async handleOpenPendingReceipt(item: OrderItemResponse) {
      this.corporateTransferOrderInfo = { orderId: item.transactionId };
      await this.checkContract();
      this.isUseCorporateTransfer = true;

      this.orderStatus = {
        subject: item.subject,
        headCount: item.headCount,
        startTime: item.orderStatus === "SUCCESS" ? item.startTime : "确认完成后生效",
        endTime: item.endTime || "-",
        corporateTransferTotalAmount: item.totalAmount / 100,
        orderStatus: item.orderStatus,
        teamName: item?.teamName,
        innerOrderId: item.innerOrderId,
        ctime: item.ctime,
        orderType: item.orderType,
        payChannel: item.payChannel || "alipay",
        payType: item.payType,
        memberType: item.memberType
      };
      if (item.orderStatus === "SUCCESS") {
        this.corporateTransferApplyStatus = CorporateTransferApplyStatus.SUCCESS;
      } else {
        this.corporateTransferApplyStatus = CorporateTransferApplyStatus.PENDING;
      }

      if (Object.values(OrderType).includes(item.orderType as OrderType)) {
        this.modalType = ModalType.API_RESOURCE_PACKAGE;
      } else {
        this.modalType = item.orderType == "team_member" ? ModalType.OPEN_TEAM_EDITION : ModalType.ADD_SEAT;
      }

      this.modalOpen = true;
      this.currentStep = 3;
    },
    /**
     * 对公支付，取消订单
     * 点击「取消订单」需弹窗二次确认，用户确认后关闭弹窗，并将订单【待支付】状态的改为【已失效】
     */
    async handleCancelOrder() {
      try {
        await Modal.confirm({
          title: "确认取消订单",
          content: "取消订单后，当前订单将失效。确认取消吗？",
          okText: "确认",
          cancelText: "取消",
          centered: true,
          zIndex: TEAM_EDITION_CONFIRM_Z_INDEX,
          onOk: async () => {
            try {
              await optCorporateOrder({
                transactionId: this.corporateTransferOrderInfo.orderId,
                orderStatus: "EXPIREPAY"
              });
              message.success("订单已取消");
              this.modalOpen = false;
              this.isUseCorporateTransfer = false;
              this.corporateTransferOrderInfo = {} as ICorporateTransferOrderInfo;
              this.createContractResponse = {} as ICreateContractResponse;

              // 如果从新客AB弹窗打开，则打开新客AB弹窗
              if (this.isOpenFromNewCustomerAB) {
                this.isOpenFromNewCustomerAB = false;
                const payStore = usePayStore();
                payStore.useProductModal("groupMember");
              }
            } catch (error) {
              console.error("取消订单失败:", error);
              return;
            }
          },
          onCancel: () => {
            console.log("取消");
          }
        });
      } catch (error) {
        message.error("取消订单失败");
        return;
      }
    },
    /**
     * 快速取消订单
     */
    async quickCancelOrder() {
      try {
        await optCorporateOrder({
          transactionId: this.corporateTransferOrderInfo.orderId,
          orderStatus: "EXPIREPAY"
        });
        this.modalOpen = false;
        this.isUseCorporateTransfer = false;
        this.corporateTransferOrderInfo = {} as ICorporateTransferOrderInfo;
        this.createContractResponse = {} as ICreateContractResponse;
      } catch (error) {
        console.error("取消订单失败:", error);
        return;
      }
    },
    /**
     * 申请合同或查看合同
     */
    async handleApplyContract() {
      if (this.createContractResponse.protocolNumber) {
        this.contractViewModalOpen = true;
      } else {
        this.contractApplyModalOpen = true;
      }
    },
    /**
     * 打开开通团队版本弹窗
     */
    async handleOpenTeamEdition() {
      this.modalType = this.isTeamVersion ? ModalType.ADD_SEAT : ModalType.OPEN_TEAM_EDITION;

      const corporateOrder = await this.judgeExistCorporateOrder();
      if (corporateOrder?.transactionId) {
        // 未支付
        if (corporateOrder.orderStatus === "NOTPAY") {
          this.corporateTransferOrderInfo = { orderId: corporateOrder.transactionId };
          this.handleOpenUploadReceipt();
          return false;
        }

        // 待确认
        if (corporateOrder?.orderStatus === "WAIT_CORPORATE_CONFIRM") {
          this.handleOpenPendingReceipt(corporateOrder);
          return false;
        }
      }

      this.handleClickEvent({
        button: "member_team_open"
      });

      const payStore = usePayStore();
      payStore.useProductModal("groupMember");
    },
    /**
     * 处理续费订购
     */
    async processRenewalOrder() {
      const corporateOrder = await this.judgeExistCorporateOrder();
      if (corporateOrder?.transactionId) {
        // 未支付
        if (corporateOrder.orderStatus === "NOTPAY") {
          this.corporateTransferOrderInfo = { orderId: corporateOrder.transactionId };
          this.handleOpenUploadReceipt();
          return false;
        }

        // 待确认
        if (corporateOrder?.orderStatus === "WAIT_CORPORATE_CONFIRM") {
          this.handleOpenPendingReceipt(corporateOrder);
          return false;
        }
      }

      this.handleClickEvent({
        button: "member_team_open"
      });

      this.modalType = ModalType.RENEWAL_ORDER;

      /**
       * 会员过期不用调
       */
      const groupStore = useGroupStore();
      if (!groupStore.isExpired) {
        await this.getTeamInfo();
      }

      const payStore = usePayStore();
      payStore.useProductModal("groupMember");
    },
    /**
     * 处理升级会员：与续费一致，优先拦截已有对公订单
     */
    async processUpgradeOrder() {
      const corporateOrder = await this.judgeExistCorporateOrder();
      if (corporateOrder?.transactionId) {
        if (corporateOrder.orderStatus === "NOTPAY") {
          this.corporateTransferOrderInfo = { orderId: corporateOrder.transactionId };
          this.handleOpenUploadReceipt();
          return false;
        }

        if (corporateOrder.orderStatus === "WAIT_CORPORATE_CONFIRM") {
          this.handleOpenPendingReceipt(corporateOrder);
          return false;
        }
      }

      this.setModalType(ModalType.UPGRADE);
      const payStore = usePayStore();
      payStore.useProductModal("groupMember");
    }
  },
  getters: {
    /**
     * 开通会员的新弹窗是否显示
     * src/views/power/components/Modal/ProductModalV2/index.vue
     */
    productModalIsOpen() {
      const payStore = usePayStore();
      return payStore.productModalState.open;
    },
    /** 团队新开通是否正打开统一支付凭证弹窗 */
    teamMemberPayModalOpen() {
      const payStore = usePayStore();
      return payStore.payModalState.open && payStore.payModalState.payMode === "teamMember";
    },
    /**
     * 团队名称
     */
    sysTeamName() {
      const userStore = useUserStore();
      // 团队名称
      const teamName = userStore.userInfo.nickname + "的团队";
      return teamName;
    },
    /**
     * 会员版本对应文案
     */
    memberVersionStr(): string {
      return getTeamVersionMeta(this.formData.selectedVersion).label;
    },
    /**
     * 剩余购买时长比率（增购席位按剩余天数折算金额）
     * 口径：一年按 372 天（与需求文档「补差价 ÷372」及后端计费一致），
     * 兼容历史 2/3 年期团队——不再依赖 totalDays/durationValue，直接 剩余天数/372
     * FIXME: 联调时与后端核对增购实付金额口径，若不一致以后端为准调整此处
     */
    remainingDaysRatio(): number {
      if (this.modalType === ModalType.RENEWAL_ORDER || this.modalType === ModalType.OPEN_TEAM_EDITION) {
        return 1;
      }
      return this.addSeat.remainingDays / TEAM_YEAR_DAYS;
    },
    /**
     * 总席位数
     * @returns
     */
    totalSeatCount(): number {
      if (
        this.modalType === ModalType.RENEWAL_ORDER ||
        this.modalType === ModalType.OPEN_TEAM_EDITION ||
        this.modalType === ModalType.UPGRADE
      ) {
        return this.formData.memberCount;
      }
      return this.addSeat.currentSeatCount + this.formData.memberCount;
    },
    /**
     * 当前折扣
     */
    /** 当前折扣展示文案，如 "7折"、"6.8折" */
    currentDiscount(): string {
      const { discount } = getPricing(this.totalSeatCount, this.formData.selectedVersion);
      return discount ? `${formatTeamDiscount(discount)}折` : "";
    },
    /** 购买时长（年）。商业化调优后仅售 1 年 */
    durationValue(): number {
      return 1;
    },
    // 付款总金额 = 折后单价 × 人数 × 年限(恒为1) × 剩余时长比率
    totalAmount(): number {
      const { selectedVersion, memberCount } = this.formData;
      if (!selectedVersion || !memberCount) {
        return 0;
      }
      const { yearlyFee } = getPricing(this.totalSeatCount, selectedVersion);
      return yearlyFee * memberCount * this.durationValue * this.remainingDaysRatio;
    },
    // 格式化后的总金额
    formattedTotalAmount(): string {
      return formatCurrency(this.totalAmount, 0);
    },

    // 划线原价 = 版本原价 × 人数 × 年限 × 剩余时长比率（「已优惠」= originalPrice - totalAmount）
    originalPrice(): number {
      const { selectedVersion, memberCount } = this.formData;
      const { basePrice } = getPricing(this.totalSeatCount, selectedVersion);
      return memberCount * basePrice * this.durationValue * this.remainingDaysRatio;
    },
    formattedOriginalPrice(): string {
      return formatCurrency(this.originalPrice, 0);
    },
    //  优惠金额 = 原价 - 总金额
    discountAmount(): number {
      return this.originalPrice - this?.totalAmount;
    },
    // 会员版本
    memberVersion(): string {
      return getTeamVersionMeta(this.formData.selectedVersion).label;
    },
    // 是否已开通团队版本
    isTeamVersion(): boolean {
      const userStore = useUserStore();
      return typeof userStore.userInfo.teamId == "number";
    },
    // 是否是超级管理员
    isSuperAdmin: () => {
      const userStore = useUserStore();
      return userStore.userInfo.superAdmin;
    },
    /**
     * 升级是否被拦截（需人工介入或不支持）
     * status=null 为尚未校验完成（loading），此时不视为拦截，仅 1/2 才拦截
     */
    isUpgradeBlocked(): boolean {
      const s = this.upgradeRefund.status;
      return s !== null && s !== 0;
    },
    /**
     * 升级预校验是否正在进行中
     */
    isUpgradeLoading(): boolean {
      return this.upgradeRefund.loading;
    },
    /**
     * 升级实付补差金额（元）。以 getCommodityInfo 返回值为准（handleUpgradeOrder 时写入）
     */
    upgradeAmountYuan(): number {
      return this.upgradeOrderAmount;
    },
    /**
     * 升级模式下人均补差（元），保留 2 位小数 = 合计 / 团队席位数
     */
    upgradeAmountPerSeatYuan(): number {
      const seat = this.totalSeatCount || 0;
      if (!seat) return 0;
      return Number((this.upgradeOrderAmount / seat).toFixed(2));
    },
    /**
     * 升级补差展示金额（元）。
     * 优先用 getCommodityInfo 写入的实付金额（upgradeOrderAmount）；
     * 下单前该值为 0，回退到预校验接口的参考补差 upRefundAmount，避免预览显示 ¥0。
     * 仅用于展示，实际支付仍以 getCommodityInfo 为准。
     */
    upgradeDisplayAmountYuan(): number {
      if (this.upgradeOrderAmount > 0) return this.upgradeOrderAmount;
      return this.upgradeRefund.detail?.upRefundAmount ?? 0;
    },
    /**
     * 升级补差人均展示金额（元），保留 2 位小数 = 展示合计 / 团队席位数
     */
    upgradeDisplayPerSeatYuan(): number {
      const seat = this.totalSeatCount || 0;
      if (!seat) return 0;
      return Number((this.upgradeDisplayAmountYuan / seat).toFixed(2));
    },
    /**
     * 购买时长选项（仅 1 年）。保留数组结构与 DiscountOption 形状，
     * 组件层（新旧两个购买弹窗）循环渲染无需感知时长数量变化
     */
    currentDiscountOptions(): DiscountOption[] {
      const pricing = getPricing(this.totalSeatCount, this.formData.selectedVersion);
      return [
        {
          value: "one_year",
          label: "1 年",
          discount: `${formatTeamDiscount(pricing.discount)}折`,
          valueNum: 1,
          price: pricing.yearlyFee,
          monthlyPrice: pricing.monthlyFee,
          originPrice: pricing.basePrice
        }
      ];
    },
    // 各版本当前档位价格（注册表驱动，新增版本无需改此处）
    currentPricingOptions(): VersionOption[] {
      return TEAM_VERSION_LIST.map((meta) => {
        const pricing = getPricing(this.totalSeatCount, meta.value);
        return {
          label: meta.label,
          value: meta.value,
          price: pricing.yearlyFee,
          monthlyPrice: pricing.monthlyFee,
          discount: pricing.discount
        };
      });
    },
    /**
     * 是否显示开通团队版本
     * 1. 当前用户是个人版
     * 2. 非paid-paid用户,userStore.showVip = true
     */
    isShowTeamVersion(): boolean {
      const userStore = useUserStore();
      return userStore.showVip;
    },
    /**
     * 是否处于团队版账户
     */
    isTeamEdition(): boolean {
      return this.userType === "team";
    }
  }
});
